import {
  createFileRoute,
  useNavigate,
  Outlet,
  useMatchRoute,
  redirect,
} from "@tanstack/react-router";
import { useStore, getCashbook, type CashEntryType } from "@/lib/pos-store";
import { PageHeader, fmt } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  ShoppingBag,
  Receipt,
  HandCoins,
  BadgeDollarSign,
  Plus,
  Lock,
} from "lucide-react";
import { hasRole } from "@/lib/auth-store";

export const Route = createFileRoute("/cashbook")({
  beforeLoad: ({ context }) => {
    if (!hasRole(context.user, "manager")) {
      throw redirect({ to: "/" });
    }
  },
  head: () => ({
    meta: [
      { title: "Cashbook" },
      {
        name: "description",
        content: "Track purchases, expenditure and debts.",
      },
    ],
  }),
  component: CashbookPage,
});

const typeMeta: Record<
  CashEntryType,
  { label: string; icon: any; tone: string }
> = {
  purchase: { label: "Purchase", icon: ShoppingBag, tone: "text-foreground" },
  expenditure: {
    label: "Expenditure",
    icon: Receipt,
    tone: "text-destructive",
  },
  debt: { label: "Debt", icon: HandCoins, tone: "text-warning-foreground" },
  debt_paid: {
    label: "Debt Paid",
    icon: BadgeDollarSign,
    tone: "text-primary",
  },
  mpesa_purchase: {
    label: "M-Pesa Purchase",
    icon: ShoppingBag,
    tone: "text-foreground",
  },
};

function CashbookPage() {
  const nav = useNavigate();
  const matchRoute = useMatchRoute();
  const cash = useStore((s) => s.cash);
  const cb = getCashbook(useStore((s) => s));

  // Only show the list when on exact /cashbook (not a child like /cashbook/new)
  const isExact = matchRoute({ to: "/cashbook", fuzzy: false });

  return (
    <div>
      {isExact ? (
        <>
          <PageHeader
            title="Cashbook"
            subtitle="Daily money in & out"
            right={
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={() => nav({ to: "/cashbook/close" })}
                >
                  <Lock className="h-3.5 w-3.5" /> Close
                </Button>
                <Button
                  size="sm"
                  type="button"
                  onClick={() => nav({ to: "/cashbook/new" })}
                >
                  <Plus className="h-4 w-4" /> Add
                </Button>
              </div>
            }
          />

          <div className="grid grid-cols-2 gap-2 px-4 pt-3">
            <Mini label="Purchases" value={cb.purchases} />
            <Mini label="Expenditure" value={cb.expenditure} tone="bad" />
            <Mini label="Debts" value={cb.debts} tone="warn" />
            <Mini label="Debts Paid" value={cb.debtsPaid} tone="good" />
          </div>

          <ul className="mt-4 divide-y divide-border px-4">
            {cash.map((c) => {
              const m = typeMeta[c.type as CashEntryType];
              const Icon = m.icon;
              return (
                <li key={c.id} className="flex items-center gap-3 py-3">
                  <div
                    className={`flex h-9 w-9 items-center justify-center rounded-full bg-secondary ${m.tone}`}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium">
                      {c.description}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {m.label} ·{" "}
                      {new Date(Number(c.at)).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  <p className={`font-display text-sm font-semibold ${m.tone}`}>
                    {fmt(Number(c.amount))}
                  </p>
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <Outlet />
      )}
    </div>
  );
}

function Mini({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "good" | "bad" | "warn";
}) {
  const c =
    tone === "good"
      ? "text-primary"
      : tone === "bad"
        ? "text-destructive"
        : tone === "warn"
          ? "text-warning-foreground"
          : "";
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className={`font-display text-base font-semibold ${c}`}>
        {fmt(value)}
      </p>
    </div>
  );
}
